import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Heart, Users, Award, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: Users,
      title: 'Leadership Development',
      description: 'We focus on cultivating leadership skills through hands-on training, mentorship, and personal growth opportunities. By fostering self-confidence, communication, and decision-making abilities, we equip young Indigenous women with the tools and confidence to lead with integrity and vision in their communities.'
    },
    {
      icon: Heart,
      title: 'Cultural Enrichment',
      description: 'We\'re committed to preserving and sharing Indigenous traditions. Through cultural activities, language learning, storytelling, and collaboration with elders and knowledge keepers, participants deepen their connection to their heritage and become ambassadors for their culture.'
    },
    {
      icon: Sparkles,
      title: 'Community Service',
      description: 'We encourage members to engage in meaningful community service projects that reflect the values of respect, responsibility, and reciprocity. These initiatives help develop a sense of civic duty and create positive change within local and tribal communities.'
    }
  ];


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: '#53a5ca' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <span className="text-sm font-semibold">First NAIWA Daughters Cherokee Collective</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              About NAIWA Daughters
            </h1>
            <p className="text-lg md:text-xl leading-relaxed opacity-95">
              We are a youth-led organization empowering young Indigenous women through leadership, advocacy, and community engagement. As the first NAIWA Daughters Cherokee collective in the country, we are pioneering a movement that will inspire Indigenous young women across Turtle Island.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="p-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#53a5ca20' }}>
                  <Target className="h-8 w-8" style={{ color: '#53a5ca' }} />
                </div>
                <h2 className="text-3xl font-bold mb-4" style={{ color: '#7e2f22' }}>Our Mission</h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Empowering young Indigenous women to become compassionate leaders who uplift their communities and preserve their heritage. Through leadership development, cultural enrichment, and community service, we strive to shape a future led by those who deeply understand and honor Indigenous ways.
                </p>
              </div>
            </Card>
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="p-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#dbbf5720' }}>
                  <Eye className="h-8 w-8" style={{ color: '#dbbf57' }} />
                </div>
                <h2 className="text-3xl font-bold mb-4" style={{ color: '#7e2f22' }}>Our Vision</h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  We envision a strong, interconnected network of NAIWA Daughters collectives across Turtle Island, cultivating young Indigenous women leaders who create lasting impact for generations to come.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* History/Story */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Our Story</h2>
              <p className="text-lg text-gray-600">Breaking new ground for Indigenous young women</p>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                NAIWA Daughters Cherokee Collective was founded by a group of enterprising young Cherokee women who saw the need where Indigenous young women could gather, grow, and lead. As the <strong>first NAIWA Daughters collective in the country</strong>, we are creating a model that will inspire similar collectives across Turtle Island.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our journey began with a simple yet powerful goal: to prioritize mental health and create a supportive community for Indigenous girls ages 10-17. Since our founding, we have assisted over 150 young women participate in cultural gatherings, community service projects, and leadership development opportunities.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                We have provided opportunities for young women to engage with leaders at the United National Indian Tribal Youth (UNITY) Conference, the Bioneers Conference, the Outdoor Economy Conference, picked up trash at Oconaluftee Island Park, created cards for tribal elders at Tsali Care, and learned traditional Cherokee arts like fingerweaving. Our young women are developing skills in public speaking, project management, and cultural advocacy that will serve them for a lifetime.
              </p>
              <p className="text-gray-700 leading-relaxed">
                As we continue to grow, our goal is to present our collective model to the national NAIWA organization so that NAIWA Daughters collectives can be established across the country. We dream of a future where young Indigenous women everywhere have access to the same supportive community and leadership opportunities that we have created here in Cherokee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NAIWA Organization Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="p-8 md:p-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: '#7e2f22' }}>About NAIWA</h2>
                <p className="text-gray-700 leading-relaxed text-lg mb-6">
                  The North American Indian Women's Association (NAIWA) is a non-profit educational and service organization founded in 1970. It aims to promote intertribal communications, improve home, family life, and community, enhance health and education, raise awareness of Indian cultures, and foster fellowship among North American Indian people. The organization is led by women from federally recognized Indian tribes and focuses on various issues such as adoption reform and food sovereignty. NAIWA has played a significant role in advocating for the rights and well-being of Native American women and continues to be a vital organization in the community.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg mb-6">
                  NAIWA Daughters Cherokee Collective operates as an <strong>auxiliary of the NAIWA NC Chapter</strong>, bridging the gap between youth and adult leadership within the organization. This relationship provides our young members with access to resources, mentorship, and a broader network of Indigenous women leaders across North Carolina and beyond.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Through this partnership, NAIWA Daughters benefits from the wisdom and experience of established NAIWA members while bringing fresh perspectives and youth energy to the larger organization's mission. Together, we work toward a future where Indigenous women of all ages are recognized as vital decision-makers in their communities and tribal nations.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Our Core Values</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The principles that guide everything we do at Naiwa Daughters
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
                    <CardContent className="pt-8 pb-8">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#53a5ca20' }}>
                        <IconComponent className="h-8 w-8" style={{ color: '#53a5ca' }} />
                      </div>
                      <h3 className="text-xl font-bold mb-3" style={{ color: '#7e2f22' }}>{value.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                    </CardContent>
                  </Card>);

              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#7e2f22' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Be Part of Our Story
            </h2>
            <p className="text-lg md:text-xl mb-10 leading-relaxed opacity-90">
              Whether you're a young Indigenous woman ready to lead, a mentor wanting to give back, or a supporter who believes in our mission—join us in building a brighter future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/join">
                <Button
                  size="lg"
                  className="text-white hover:opacity-90 transition-all duration-200"
                  style={{ backgroundColor: '#53a5ca' }}>Join NAIWA Daughters


                </Button>
              </Link>
              <Link to="/mentor">
                <Button
                  size="lg"
                  className="bg-white hover:bg-gray-100 transition-all duration-200"
                  style={{ color: '#7e2f22' }}>

                  Become a Mentor
                </Button>
              </Link>
              <Link to="/support">
                <Button
                  size="lg"
                  className="text-white hover:opacity-90 transition-all duration-200"
                  style={{ backgroundColor: '#e37c55' }}>

                  Support Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default About;